<template>
  <PublicLayout
    variant="no-bg"
    :hero="false"
    breadcrumbs-in-top
    :ui="{
      main: 'pt-0!',
    }"
  >
    <UContainer class="mb-0!">
      <SPBreadcrumbs class="mb-3!" dark :breadcrumbs="breadcrumbs" />

      <UCard
        class="rounded-[24px] mb-15 max-xl:mb-10 p-10 max-lg:p-2.5"
        :ui="{
          body: 'max-lg:p-0!',
        }"
      >
        <div
          class="flex w-full h-96.5 items-center justify-between mb-10 max-lg:mb-8 max-lg:flex-col max-lg:h-auto"
        >
          <div class="max-lg:order-2">
            <h2 class="typo-heading-h2 mb-4">
              {{ pageData?.name }}
            </h2>
            <p
              class="w-6/7 max-md:w-full typo-paragraph-md-regular text-content-default-secondary mb-8"
            >
              {{ pageData?.description }}
            </p>

            <UButton class="px-4.5 w-max max-md:w-full" @click="signForClass">
              Записаться
            </UButton>
          </div>

          <img
            v-if="pageData?.image_url"
            class="h-full max-w-96.5 object-cover object-right rounded-[16px] max-lg:order-1 max-lg:max-w-full max-lg:mb-8"
            :src="pageData?.image_url"
            alt=""
          />
        </div>

        <div class="mt-auto flex max-lg:flex-col gap-5 max-lg:gap-2">
          <NuxtLink
            v-for="(card, index) in cards"
            :key="index"
            class="w-full block"
            :to="card.to"
          >
            <SmallCard
              :icon="card.icon"
              :top="card.top"
              :bottom="card.bottom"
              :ui="{
                root: 'h-22! max-xl:h-18! w-full! block border border-muted',
                top: 'text-lg! text-neutral-default! font-medium mb-0!',
                bottom: 'font-lato! text-neutral-light text-sm',
                icon: card.iconClasses,
                iconWrapper: ['text-white!', card.iconWrapper].join(' '),
              }"
            />
          </NuxtLink>
        </div>
      </UCard>

      <DetailTabs
        v-model="activeTab"
        :items="TRAINER_TABS"
        :content="false"
        tabs-class="w-max mb-10 max-xl:mb-7 max-md:mb-5"
      />
    </UContainer>

    <div class="min-h-100">
      <NuxtPage />
    </div>
  </PublicLayout>
</template>

<script setup lang="ts">
import type { BreadcrumbItem } from '~/types'
import PublicLayout from '~/layouts/PublicLayout.vue'
import DetailTabs from '~/components/DetailTabs.vue'
import { declOfNumWithVal } from '~/shared'
import SignForClassModal from '~/components/SignForClassModal.vue'
import { useDetailTabs } from '~/composables/useDetailTabs'
import { TRAINER_TABS } from '~/shared/trainer'
import { useModal } from '~/shared/modals'

defineOptions({
  name: 'TrainerDetail',
})

const { routeId } = useRouteId()

const trainerStore = useTrainerStore()
const { pageData } = storeToRefs(trainerStore)

const breadcrumbs = computed((): BreadcrumbItem[] => [
  { name: 'Тренеры', to: '/trainers/' },
  { name: pageData.value?.name ?? '' },
])

await useAppAsyncData(`trainer-${routeId}`, async () => {
  await Promise.all([trainerStore.fetchTrainer(routeId)])
})

const cards = computed(() => [
  {
    top: declOfNumWithVal(pageData.value?.experience_years ?? 0, [
      'школа',
      'школы',
      'школ',
    ]),
    bottom: 'Тренерской работы',
    icon: 'i-local-graduation-cap',
    iconClasses: '',
    iconWrapper: 'bg-success',
    to: `/trainers/${routeId}/schools`,
  },
  {
    top: declOfNumWithVal(pageData.value?.directions?.length ?? 0, [
      'направление',
      'направления',
      'направлений',
    ]),
    bottom: 'Спортивных дисциплин',
    icon: 'i-local-dumbbell',
    iconClasses: '',
    iconWrapper: 'bg-accent-light',
    to: `/trainers/${routeId}/directions`,
  },
  {
    top: declOfNumWithVal(pageData.value?.students_count ?? 0, [
      'ученик',
      'ученика',
      'учеников',
    ]),
    bottom: 'Занимаются у тренера',
    icon: 'i-local-users',
    iconWrapper: 'bg-[#03A2AB]!',
    to: `/trainers/${routeId}/our-pride`,
  },
])

const { activeTab } = useDetailTabs({
  tabs: TRAINER_TABS,
  basePath: '/trainers',
})

const signForClass = () => {
  const { open } = useModal(SignForClassModal, {
    props: {
      id: routeId,
      apiMethod: trainerStore.trainingSignup,
    },
  })

  open()
}
</script>

<style lang="scss" scoped>
.detail-tabs {
  --ui-text-muted: var(--color-neutral-default);
}
</style>
